import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Music, Plus, Trash2, Globe, Lock, ExternalLink, X } from "lucide-react";
import { toast } from "sonner";

/**
 * PlaylistsCard — customer profile widget for creating and managing playable
 * playlists of tracks from YouTube, Spotify, SoundCloud, Apple Music and
 * Bandcamp. Each track is stored as a URL; the backend detects the source
 * and returns an embeddable player URL where possible. Users can toggle a
 * playlist between private (owner-only) and public (visible on their profile).
 */
const SOURCE_LABEL = {
  youtube: "YouTube",
  spotify: "Spotify",
  soundcloud: "SoundCloud",
  apple_music: "Apple Music",
  bandcamp: "Bandcamp",
  other: "Link",
};

export default function PlaylistsCard({ profileUserId, viewOnly = false }) {
  const { user } = useAuth();
  const targetId = profileUserId || user?.id;
  const isMine = !viewOnly && !!user && user.id === targetId;

  const [playlists, setPlaylists] = useState([]);
  const [activePid, setActivePid] = useState(null);
  const [activeDetail, setActiveDetail] = useState(null);
  const [loadingList, setLoadingList] = useState(true);

  const loadPlaylists = async () => {
    setLoadingList(true);
    try {
      const url = isMine ? "/playlists/mine" : `/playlists/user/${targetId}`;
      const r = await api.get(url);
      setPlaylists(r.data?.items || []);
      if (!activePid && r.data?.items?.length) setActivePid(r.data.items[0].id);
    } catch (e) {
      if (isMine) toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoadingList(false); }
  };

  const loadDetail = async (pid) => {
    if (!pid) { setActiveDetail(null); return; }
    try {
      const r = await api.get(`/playlists/${pid}`);
      setActiveDetail(r.data);
    } catch (e) {
      setActiveDetail(null);
    }
  };

  useEffect(() => { if (targetId) loadPlaylists(); }, [targetId, isMine]);
  useEffect(() => { loadDetail(activePid); }, [activePid]);

  if (!targetId) return null;
  if (!isMine && playlists.length === 0 && !loadingList) return null; // hide widget entirely if no public playlists

  return (
    <Card className="rounded-md p-6" data-testid="playlists-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Music size={16} className="text-[#B31E1E]" />
            <h3 className="font-display text-xl">{isMine ? "My playlists" : "Playlists"}</h3>
          </div>
          <p className="mt-1 text-sm text-gray-600 max-w-2xl">
            {isMine
              ? "Curate music from YouTube, Spotify, SoundCloud and Apple Music. Turn any playlist public to show it on your profile."
              : "Music this member is sharing on their profile."}
          </p>
        </div>
        {isMine && <CreatePlaylistDialog onCreated={loadPlaylists} />}
      </div>

      <div className="mt-5 grid grid-cols-1 md:grid-cols-[220px_1fr] gap-5">
        {/* Playlist sidebar */}
        <div>
          {loadingList ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : playlists.length === 0 ? (
            <p className="text-sm text-gray-500" data-testid="playlists-empty">{isMine ? "Create your first playlist to get started." : "No public playlists yet."}</p>
          ) : (
            <ul className="space-y-1" data-testid="playlists-list">
              {playlists.map((p) => (
                <li key={p.id}>
                  <button
                    onClick={() => setActivePid(p.id)}
                    className={`w-full text-left px-3 py-2 rounded-md text-sm border ${activePid === p.id ? "border-[#B31E1E] bg-[#B31E1E]/5 font-medium" : "border-transparent hover:border-border"}`}
                    data-testid={`playlist-item-${p.id}`}
                  >
                    <div className="flex items-center gap-2 truncate">
                      {p.is_public ? <Globe size={11} className="text-emerald-600 shrink-0" /> : <Lock size={11} className="text-gray-400 shrink-0" />}
                      <span className="truncate">{p.name}</span>
                    </div>
                    <div className="text-[11px] text-gray-500 mt-0.5">{p.item_count} {p.item_count === 1 ? "track" : "tracks"}</div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Active playlist detail */}
        <div className="min-w-0">
          {!activeDetail ? (
            <p className="text-sm text-gray-500">Select a playlist to see its tracks.</p>
          ) : (
            <PlaylistDetail
              detail={activeDetail}
              isMine={isMine && activeDetail.playlist?.is_mine}
              onChange={async () => { await loadDetail(activePid); await loadPlaylists(); }}
              onDeleted={async () => { setActivePid(null); await loadPlaylists(); }}
            />
          )}
        </div>
      </div>
    </Card>
  );
}

function CreatePlaylistDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isPublic, setIsPublic] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!name.trim()) return toast.error("Name your playlist");
    setSaving(true);
    try {
      await api.post("/playlists", { name: name.trim(), description: description.trim(), is_public: isPublic });
      toast.success("Playlist created");
      setName(""); setDescription(""); setIsPublic(false); setOpen(false);
      onCreated();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-[35px]" data-testid="create-playlist-button">
          <Plus size={13} className="mr-2" /> New playlist
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md rounded-md">
        <DialogHeader><DialogTitle>New playlist</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Coding Chill · Road Trip · Rock Legends" data-testid="new-playlist-name" className="rounded-sm mt-1" />
          </div>
          <div>
            <Label className="text-xs">Description (optional)</Label>
            <Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="new-playlist-desc" className="rounded-sm mt-1" />
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} data-testid="new-playlist-public" />
            Show on my public profile
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="rounded-[35px] bg-[#B31E1E] hover:bg-[#8F1717] text-white" data-testid="new-playlist-submit">
            {saving ? "Saving…" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PlaylistDetail({ detail, isMine, onChange, onDeleted }) {
  const { playlist, items } = detail;
  const [addOpen, setAddOpen] = useState(false);

  const del = async () => {
    if (!confirm(`Delete "${playlist.name}"? Its tracks will be removed too.`)) return;
    try {
      await api.delete(`/playlists/${playlist.id}`);
      toast.success("Deleted");
      onDeleted();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const togglePublic = async () => {
    try {
      await api.patch(`/playlists/${playlist.id}`, { is_public: !playlist.is_public });
      toast.success(playlist.is_public ? "Playlist made private" : "Playlist is now public");
      onChange();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const removeTrack = async (iid) => {
    if (!confirm("Remove this track?")) return;
    try {
      await api.delete(`/playlists/${playlist.id}/items/${iid}`);
      onChange();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="playlist-detail">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="font-display text-lg">{playlist.name}</h4>
            {playlist.is_public ? (
              <Badge className="rounded-sm bg-emerald-100 text-emerald-800 border border-emerald-200"><Globe size={9} className="mr-1" /> Public</Badge>
            ) : (
              <Badge variant="outline" className="rounded-sm"><Lock size={9} className="mr-1" /> Private</Badge>
            )}
          </div>
          {playlist.description && <p className="text-xs text-gray-600 mt-1">{playlist.description}</p>}
          <div className="text-[11px] text-gray-500 mt-1">{items.length} {items.length === 1 ? "track" : "tracks"}</div>
        </div>
        {isMine && (
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" variant="outline" className="rounded-sm h-8" onClick={togglePublic} data-testid="toggle-public-button">
              {playlist.is_public ? "Make private" : "Make public"}
            </Button>
            <Button size="sm" onClick={() => setAddOpen(true)} className="rounded-sm h-8 bg-[#B31E1E] hover:bg-[#8F1717] text-white" data-testid="add-track-button">
              <Plus size={12} className="mr-1" /> Add track
            </Button>
            <Button size="sm" variant="ghost" onClick={del} className="text-red-600 hover:text-red-700 h-8" data-testid="delete-playlist-button">
              <Trash2 size={12} />
            </Button>
          </div>
        )}
      </div>

      <div className="mt-4 space-y-3" data-testid="playlist-tracks">
        {items.length === 0 && (
          <p className="text-sm text-gray-500">{isMine ? "Add a YouTube, Spotify, SoundCloud or Apple Music link to get started." : "No tracks in this playlist yet."}</p>
        )}
        {items.map((it, idx) => (
          <TrackRow key={it.id} item={it} index={idx + 1} canRemove={isMine} onRemove={() => removeTrack(it.id)} />
        ))}
      </div>

      {isMine && (
        <AddTrackDialog
          open={addOpen}
          onOpenChange={setAddOpen}
          playlistId={playlist.id}
          onAdded={() => { setAddOpen(false); onChange(); }}
        />
      )}
    </div>
  );
}

function TrackRow({ item, index, canRemove, onRemove }) {
  const label = SOURCE_LABEL[item.source_type] || "Link";
  const hasEmbed = !!item.embed_url;

  return (
    <div className="rounded-md border border-border p-3" data-testid={`track-${item.id}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="min-w-0 flex-1">
          <div className="text-[11px] text-gray-500 flex items-center gap-2">
            <span className="font-mono">#{index}</span>
            <Badge variant="outline" className="rounded-sm text-[10px]">{label}</Badge>
            <a href={item.url} target="_blank" rel="noreferrer" className="hover:text-[#B31E1E] truncate inline-flex items-center gap-1" data-testid={`track-source-${item.id}`}>
              Open <ExternalLink size={9} />
            </a>
          </div>
          {(item.title || item.artist) && (
            <div className="text-sm text-gray-800 mt-1 truncate">
              {item.title || item.url}{item.artist ? ` — ${item.artist}` : ""}
            </div>
          )}
        </div>
        {canRemove && (
          <button onClick={onRemove} className="text-gray-400 hover:text-red-600 shrink-0" data-testid={`remove-track-${item.id}`}>
            <X size={14} />
          </button>
        )}
      </div>
      {hasEmbed ? (
        <div className="rounded-md overflow-hidden bg-black">
          {item.source_type === "youtube" && (
            <div className="aspect-video">
              <iframe
                src={item.embed_url}
                title={item.title || "YouTube track"}
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                loading="lazy"
              />
            </div>
          )}
          {item.source_type === "spotify" && (
            <iframe
              src={item.embed_url}
              title={item.title || "Spotify track"}
              className="w-full h-[152px]"
              frameBorder="0"
              allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
              loading="lazy"
            />
          )}
          {item.source_type === "soundcloud" && (
            <iframe
              src={item.embed_url}
              title={item.title || "SoundCloud track"}
              className="w-full h-[166px]"
              frameBorder="0"
              allow="autoplay"
              loading="lazy"
            />
          )}
          {item.source_type === "apple_music" && (
            <iframe
              src={item.embed_url}
              title={item.title || "Apple Music track"}
              className="w-full h-[175px]"
              frameBorder="0"
              allow="autoplay *; encrypted-media *;"
              loading="lazy"
              sandbox="allow-forms allow-popups allow-same-origin allow-scripts allow-top-navigation-by-user-activation"
            />
          )}
        </div>
      ) : (
        <a href={item.url} target="_blank" rel="noreferrer" className="text-xs text-[#B31E1E] hover:underline break-all">
          {item.url}
        </a>
      )}
    </div>
  );
}

function AddTrackDialog({ open, onOpenChange, playlistId, onAdded }) {
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [artist, setArtist] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!url.trim()) return toast.error("Paste a music link");
    setSaving(true);
    try {
      await api.post(`/playlists/${playlistId}/items`, {
        url: url.trim(),
        title: title.trim() || undefined,
        artist: artist.trim() || undefined,
      });
      setUrl(""); setTitle(""); setArtist("");
      onAdded();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-md">
        <DialogHeader><DialogTitle>Add a track</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Link (YouTube · Spotify · SoundCloud · Apple Music)</Label>
            <Input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://open.spotify.com/track/…"
              data-testid="new-track-url"
              className="rounded-sm mt-1"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Title (optional)</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="new-track-title" className="rounded-sm mt-1" />
            </div>
            <div>
              <Label className="text-xs">Artist (optional)</Label>
              <Input value={artist} onChange={(e) => setArtist(e.target.value)} data-testid="new-track-artist" className="rounded-sm mt-1" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="rounded-[35px] bg-[#B31E1E] hover:bg-[#8F1717] text-white" data-testid="new-track-submit">
            {saving ? "Adding…" : "Add track"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
